import { computed, toRef } from 'vue';
import { useRowSelection } from './useRowSelection'; // Import the selection composable

/**
 * A composable for managing data table logic, including sorting,
 * filtering, and row selection.
 *
 * @param {object} options - An object containing reactive Refs to props and the emit function.
 * @param {import('vue').Ref<Array>} options.rows - Ref to the rows prop.
 * @param {import('vue').Ref<Array>} options.fields - Ref to the fields prop.
 * @param {import('vue').Ref<string>} options.orderBy - Ref to the orderBy prop.
 * @param {import('vue').Ref<string>} options.orderDirection - Ref to the orderDirection prop.
 * @param {import('vue').Ref<object>} options.filters - Ref to the filters prop.
 * @param {import('vue').Ref<boolean>} options.selectable - Ref to the selectable prop.
 * @param {import('vue').Ref<Array>} options.selectedRows - Ref to the selectedRows v-model prop (array of IDs).
 * @param {Function} options.emit - The component's emit function.
 */
export function useDataTable({
  rows,
  fields,
  orderBy,
  orderDirection,
  filters,
  selectable,
  selectedRows,
  emit
}) {

  // 1. Row Selection Logic
  // We just instantiate the other composable and pass on the
  // relevant props (as Refs) and the emit function.
  const {
    selectedRowsLocal,
    toggleRowSelection,
    isRowSelected,
    selectAll,
    clearSelection,
  } = useRowSelection(selectedRows, emit);

  // 2. Computed Properties (replaces `computed`)
  const fieldsSearchable = computed(() => {
    return fields.value.filter((field) => field.searchable);
  });

  const fieldsSortable = computed(() => {
    return fields.value.filter((field) => field.sortable);
  });

  // 3. Methods (replaces `methods`)
  const sortChanged = (newOrderBy, newOrderDirection) => {
    emit('sort-changed', { orderBy: newOrderBy, orderDirection: newOrderDirection });
  };

  const searchChanged = (newSearchQuery) => {
    emit('search-changed', newSearchQuery);
  }

  const filterChanged = (field, value) => {
    // Create a new filters object based on the current prop
    const newFilters = { ...filters.value };

    // Add, update, or remove the filter
    if (typeof value === 'string' && value.length === 0) {
      delete newFilters[field];
    } else {
      newFilters[field] = value;
    }

    emit('filters-changed', newFilters);
  };

  /**
   * Helper to get a row's value, handling nested keys, fallbacks, and formatters.
   * This logic was in your template, so we provide it here.
   */
  const rowFieldValue = (row, fieldName, fallback = '', formatter = null) => {
    if (!row) return fallback;

    // Handle nested keys (e.g., 'user.name')
    const value = fieldName.split('.').reduce((acc, part) =>
      (acc && acc[part] !== null && acc[part] !== undefined) ? acc[part] : undefined,
    row
    );

    // Apply formatter if one is provided
    if (formatter && typeof formatter === 'function') {
      return formatter(value, row);
    }

    // Handle fallback for null/undefined values
    if (value === undefined || value === null) {
      return fallback;
    }

    return value;
  };

  // 4. Return all state and methods the component template needs
  return {
    // Computed
    fieldsSearchable,
    fieldsSortable,

    // Methods
    sortChanged,
    searchChanged,
    filterChanged,
    rowFieldValue,

    // State & Methods from useRowSelection
    selectedRowsLocal,
    toggleRowSelection,
    isRowSelected,
    selectAll,
    clearSelection,
  };
}
